<template>
  <label :for="id" @click="emit('update:modelValue', value)"
    class="flex cursor-pointer items-center gap-3 px-4 py-3.5 rounded-[14px] radio-card active:scale-[0.98] transition-all duration-200 ease-in-out"
    :class="{ 'radio-card--selected': modelValue == value }">

    <!-- Radio indicator -->
    <div class="shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center radio-dot transition-all duration-200"
      :class="modelValue == value ? 'radio-dot--active' : 'radio-dot--inactive'">
      <div class="rounded-full transition-all duration-200"
        :class="modelValue == value ? 'w-2 h-2 bg-white' : 'w-0 h-0'"></div>
    </div>

    <!-- Text content -->
    <div class="flex flex-col min-w-0">
      <span class="text-[13px] font-semibold text-foreground leading-tight">{{ label }}</span>
      <span v-if="description" class="text-[12px] text-muted-foreground leading-snug mt-0.5">{{ description }}</span>
    </div>
  </label>
</template>

<script setup lang="ts">
export type RadioOption = {
  id: string,
  name: string,
  value: string,
  label: string,
  description?: string
}

defineProps<{
  id: string,
  name: string,
  value: string,
  label: string,
  modelValue: string
  description?: string
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void,
}>();
</script>

<style scoped>
/* ── Light mode ── */
.radio-card {
  background: #FFFFFF;
  border: 1.5px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
}
.radio-card--selected {
  border-color: rgba(6, 200, 255, 0.35);
  box-shadow: 0 1px 8px rgba(6, 200, 255, 0.10), 0 0 0 1px rgba(6, 200, 255, 0.08);
  background: linear-gradient(135deg, rgba(6, 200, 255, 0.03) 0%, #FFFFFF 100%);
}
.radio-dot--inactive {
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: transparent;
}
.radio-dot--active {
  background: linear-gradient(135deg, #06C8FF 0%, #009FE3 100%);
  border: none;
  box-shadow: 0 2px 6px rgba(6, 200, 255, 0.30);
}

/* ── Dark mode ── */
:is(.dark *).radio-card {
  background: #1E2024;
  border: 1.5px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
}
:is(.dark *).radio-card--selected {
  border-color: rgba(6, 200, 255, 0.25);
  box-shadow: 0 1px 10px rgba(6, 200, 255, 0.08), 0 0 0 1px rgba(6, 200, 255, 0.06);
  background: linear-gradient(135deg, rgba(6, 200, 255, 0.06) 0%, #1E2024 100%);
}
:is(.dark *).radio-dot--inactive {
  border: 1.5px solid rgba(255, 255, 255, 0.18);
  background: transparent;
}
:is(.dark *).radio-dot--active {
  box-shadow: 0 2px 8px rgba(6, 200, 255, 0.20);
}
</style>
